import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import produce from 'immer';
import { merge } from 'lodash';

import { Button } from '~/components';
import FormDrawer from '~/components/FormDrawer';
import { PlusIcon } from '~/img';
import { ApplicationPayload } from '~/types/ApplicationPayload';

import buildNamePath from '~/utils/buildNamePath';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { Shareholders } from '~/types/Shareholders';
import { useGetApplication } from '~/controllers/wizard';
import { hasSoftDeclineFields } from '~/utils/softDeclinesFields';
import { shareholdersKey } from '.';
import BusinessForm, { formName } from './BusinessForm';
import { isShareholderEditButtonDisabled } from './utils';

const BusinessAdd: FC<Partial<FormItemInjectedProps<Shareholders>>> = (props) => {
  const { value, onChange } = props as FormItemInjectedProps<Shareholders>;
  const shareholdersFormState = { shareholders: value };

  const { t } = useTranslation('fillApplication');
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const newShareholderIndex = shareholdersFormState.shareholders?.businesses?.length ?? 0;

  const onFinish = (newValues: ApplicationPayload) => {
    const updatedData = produce(shareholdersFormState, (draft) => {
      merge(draft, newValues);
    });
    onChange?.(updatedData.shareholders);
    handleClose();
  };

  const applicationQuery = useGetApplication();
  const hasBusinessValidationWarnings = hasSoftDeclineFields(applicationQuery.data, 'shareholders.businesses[]');

  return (
    <>
      <Button
        id="addNewBusinessShareholderButton"
        extraLarge
        type="default"
        icon={<PlusIcon />}
        disabled={isShareholderEditButtonDisabled(applicationQuery.data, hasBusinessValidationWarnings)}
        onClick={handleOpen}
      >
        {t('Add Legal entity shareholder')}
      </Button>
      <FormDrawer
        id="businessAddDrawer"
        visible={open}
        onClose={handleClose}
        title={t('Add Legal entity shareholder')}
        formName={formName}
      >
        <BusinessForm
          onFinish={onFinish}
          initialValues={shareholdersFormState}
          n={buildNamePath([shareholdersKey, 'businesses', newShareholderIndex])}
        />
      </FormDrawer>
    </>
  );
};

export default BusinessAdd;
